// Shared Plan Builder form â€” the ONE plan build/edit UI, used by the
// superadmin Plan Library page and the broker Add Plan dialog so both portals
// build plans identically (same fields, same rate/benefit editors, same CSV
// import). `variant` gates the superadmin-only Forms tab (platform form
// library attach); everything else is portal-agnostic â€” server-side ownership
// checks (computePlanAccess) remain the authority on what a save may touch.

import { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { queryClient, apiRequest } from '@/lib/queryClient';
import { getBenefitFieldTemplate } from '@shared/constants/benefitFieldTemplates';
import { AGE_DETERMINATION_OPTIONS } from '@shared/lib/ageDetermination';
import { planBuilderMissingFields, planTypeNeedsNetworkType } from '@shared/utils/planReview';
import { RATE_STRUCTURE_TYPE_OPTIONS, PLAN_TYPE_OPTIONS } from '@shared/schemas/plans';
import { AGE_BAND_PRESETS } from '@/lib/ageBands';
import { TABLE_RATE_LABELS } from '@shared/constants/coverageTable';
import { TemplatedBenefitsEditor } from '@/components/benefits/TemplatedBenefitsEditor';
import SystemPlanIdBadge from '@/components/plans/SystemPlanIdBadge';
import { PlanDocumentsManager } from '@/components/plans/PlanDocuments';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  RATE_CADENCES,
  periodLabel,
  periodWindow,
  periodsPerCadence,
  type RateCadence,
} from '@shared/utils/rateCadence';

const CADENCE_LABELS: Record<RateCadence, string> = {
  annual: 'Annual (1 set)',
  biannual: 'Biannual (2 sets)',
  quarterly: 'Quarterly (4 sets)',
};
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import {
  Plus,
  FileText,
  Trash2,
  DollarSign,
  X,
  MapPin,
  Upload,
} from 'lucide-react';

import {
  COVERAGE_TIERS,
  RATE_CSV_TEMPLATE,
  RATE_CSV_TEMPLATE_TABLE,
  RATE_CSV_TEMPLATE_WIDE,
  ageBandStart,
  dedupeAndSortRates,
  parseRateCsv,
  planCsvImport,
  type PlanRate,
} from '@/lib/planRateCsvImport';

// Pure rate helpers live in @/lib/planRateCsvImport (unit-tested there);
// re-exported so existing importers of this module keep working.
export { COVERAGE_TIERS, ageBandStart, dedupeAndSortRates };
export type { PlanRate };

export interface PlanBenefit {
  id?: number;
  benefitType: string;
  inNetworkDeductible?: string | null;
  outNetworkDeductible?: string | null;
  inNetworkCoinsurance?: string | null;
  outNetworkCoinsurance?: string | null;
  inNetworkCopay?: string | null;
  outNetworkCopay?: string | null;
  maxOutOfPocket?: string | null;
  annualMaximum?: string | null;
  deductibleApply?: boolean;
  description?: string | null;
}

// Raw Word & Brown benefit line, kept verbatim so nothing is lost on import.
export interface RawBenefit {
  name: string;
  value: string;
  network?: "in" | "out";
  deductibleApply?: boolean;
}

export interface LibraryPlan {
  id: number;
  name: string;
  type: string;
  carrierId: number;
  carrierName: string | null;
  planCode: string;
  networkType: string | null;
  network?: string | null;
  metalTier: string | null;
  fundingType?: string | null;
  minimumValue?: boolean | null;
  minimumEssentialCoverage?: boolean | null;
  description: string | null;
  rateStructureType: string | null;
  ageDetermination: string | null;
  isActive: boolean;
  effectiveDate: string | null;
  endDate: string | null;
  availableStates: string[] | null;
  /** Full rows â€” present on the plan-DETAIL response, not the list. */
  rates: PlanRate[];
  /**
   * Aggregate for the library list's "Rates" column. The list endpoint sends
   * this INSTEAD of `rates`: shipping every row was ~780k records per load.
   */
  rateSummary?: {
    total: number;
    byStructure: Record<string, { count: number; employeeOnlyRate: string | null }>;
  };
  benefits?: PlanBenefit[];
  source?: string | null;
  lastSyncedAt?: string | null;
  rawBenefits?: RawBenefit[] | null;
  createdAt: string;
  linkedEmployerCount?: number;
  /** DEV-388 two-person review: draft | needs_review | approved | changes_requested (null = not reviewed). */
  reviewStatus?: string | null;
  /** List endpoint: open pending version of an Approved plan. */
  pendingReview?: { status: string; urgent: boolean } | null;
  /** List endpoint: no SBC/document and no system form attached. */
  needsForms?: boolean;
  /** Detail endpoint: open pending version summary. */
  pendingVersion?: { id: number; status: string; urgent: boolean; changedSections: string[] } | null;
}

export interface RateVersion {
  id: number;
  planId: number;
  clientId: number | null;
  effectiveStart: string;
  effectiveEnd: string | null;
  revision: number;
  correctionOfId: number | null;
  correctionReason: string | null;
  label: string | null;
  publishedAt: string | null;
}

export interface Carrier {
  id: number;
  name: string;
  types: string[];
  isActive: boolean;
}

// "medical" is an alias of "health" (normalizePlanType); hide the duplicate.
export const PLAN_TYPES = PLAN_TYPE_OPTIONS.filter((option) => option.value !== "medical");

export const NETWORK_TYPES = [
  { value: "HMO", label: "HMO" },
  { value: "PPO", label: "PPO" },
  { value: "EPO", label: "EPO" },
  { value: "POS", label: "POS" },
  { value: "HDHP", label: "HDHP" },
];

export const METAL_TIERS = [
  { value: "Bronze", label: "Bronze" },
  { value: "Silver", label: "Silver" },
  { value: "Gold", label: "Gold" },
  { value: "Platinum", label: "Platinum" },
];

export const RATE_STRUCTURE_TYPES = RATE_STRUCTURE_TYPE_OPTIONS;

export const TYPE_COLORS: Record<string, string> = {
  health: "bg-blue-100 text-blue-700",
  medical: "bg-blue-100 text-blue-700",
  dental: "bg-green-100 text-green-700",
  vision: "bg-purple-100 text-purple-700",
  life: "bg-yellow-100 text-yellow-700",
  disability: "bg-orange-100 text-orange-700",
  voluntary: "bg-teal-100 text-teal-700",
};

export const DEFAULT_AGE_BANDS = [
  "0-17", "18-24", "25-29", "30-34", "35-39", "40-44", "45-49", "50-54", "55-59", "60-64", "64+"
];

// Per-single-age bands ("0".."63","64+") â€” the granularity Word & Brown uses.
export const ALL_AGE_BANDS = [
  ...Array.from({ length: 64 }, (_, age) => String(age)),
  "64+",
];

export const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME",
  "MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI",
  "SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];


export function PlanBuilderForm({
  carriers,
  initialPlan,
  onSave,
  isPending,
  planId,
  variant = "superadmin",
}: {
  carriers: Carrier[];
  initialPlan?: LibraryPlan;
  onSave: (data: any) => void;
  isPending: boolean;
  planId?: number;
  /**
   * "superadmin" (default) shows the platform Forms tab; "broker" hides it â€”
   * brokers attach forms from their plan page after creation. All other tabs
   * are identical across portals.
   */
  variant?: "superadmin" | "broker";
}) {
  const { toast } = useToast();
  const isSuperadminVariant = variant === "superadmin";

  const { data: attachedForms = [] } = useQuery<any[]>({
    queryKey: ["/api/superadmin/plan-library", planId, "forms"],
    queryFn: async () => {
      const res = await fetch(`/api/superadmin/plan-library/${planId}/forms`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch forms");
      return res.json();
    },
    enabled: !!planId && isSuperadminVariant,
  });

  // Who a rate change reaches: active enrollments + employers on this plan
  // (directly or via legacy clones). Shown as the "you're about to change
  // rates clients are using" prompt in the Rates tab.
  const { data: rateImpact } = useQuery<{
    planId: number;
    linkedClientPlans: number;
    affectedEnrollments: number;
    affectedClients: number;
  }>({
    queryKey: ["/api/plans", planId, "rate-version-impact"],
    queryFn: async () => {
      const res = await fetch(`/api/plans/${planId}/rate-version-impact`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch rate impact");
      return res.json();
    },
    enabled: !!planId,
  });

  const { data: suggestedForms = [] } = useQuery<any[]>({
    queryKey: ["/api/superadmin/plan-library", planId, "forms", "suggestions"],
    queryFn: async () => {
      const res = await fetch(`/api/superadmin/plan-library/${planId}/forms/suggestions`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch suggestions");
      return res.json();
    },
    enabled: !!planId && isSuperadminVariant,
  });

  const attachFormMutation = useMutation({
    mutationFn: async (formId: number) => {
      const res = await apiRequest("POST", `/api/superadmin/plan-library/${planId}/forms`, { formId, isRequired: true, autoAttach: true });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/superadmin/plan-library", planId, "forms"] });
      queryClient.invalidateQueries({ queryKey: ["/api/superadmin/plan-library", planId, "forms", "suggestions"] });
      // DEV-388: the Needs Forms flag and plan history follow attachments.
      queryClient.invalidateQueries({ queryKey: ["/api/superadmin/plan-library", planId, "review"] });
      toast({ title: "Form attached to plan" });
    },
    onError: (error: any) => {
      toast({ title: "Failed to attach form", description: error.message, variant: "destructive" });
    },
  });

  const removeFormMutation = useMutation({
    mutationFn: async (attachmentId: number) => {
      const res = await apiRequest("DELETE", `/api/superadmin/plan-library/${planId}/forms/${attachmentId}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/superadmin/plan-library", planId, "forms"] });
      queryClient.invalidateQueries({ queryKey: ["/api/superadmin/plan-library", planId, "forms", "suggestions"] });
      // DEV-388: the Needs Forms flag and plan history follow attachments.
      queryClient.invalidateQueries({ queryKey: ["/api/superadmin/plan-library", planId, "review"] });
      toast({ title: "Form removed from plan" });
    },
    onError: (error: any) => {
      toast({ title: "Failed to remove form", description: error.message, variant: "destructive" });
    },
  });
  const [name, setName] = useState(initialPlan?.name || "");
  const [type, setType] = useState(initialPlan?.type || "health");
  const [carrierId, setCarrierId] = useState<number | null>(initialPlan?.carrierId || null);

  // Create mode (no planId yet): forms are picked here and attached right
  // after the plan is created â€” the create payload carries their ids.
  const [pendingForms, setPendingForms] = useState<any[]>([]);
  const [formSearch, setFormSearch] = useState("");
  const { data: pickableForms = [] } = useQuery<any[]>({
    queryKey: ["/api/superadmin/forms", { search: formSearch, carrierId: carrierId ?? null }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (formSearch) params.set("search", formSearch);
      if (carrierId) params.set("carrierId", String(carrierId));
      const res = await fetch(`/api/superadmin/forms?${params.toString()}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch forms");
      return res.json();
    },
    enabled: !planId && isSuperadminVariant,
  });
  const [planCode, setPlanCode] = useState(initialPlan?.planCode || "");
  const [networkType, setNetworkType] = useState(initialPlan?.networkType || "");
  // Carrier's NAMED network ("Select HMO", "California Care HMO") â€” free text
  // with suggestions from networks already used for the selected carrier, so
  // the first plan types it and every later plan picks it.
  const [network, setNetwork] = useState(initialPlan?.network || "");
  const { data: networkSuggestions = [] } = useQuery<string[]>({
    queryKey: ["/api/plan-networks", carrierId ?? null],
    queryFn: async () => {
      const params = carrierId ? `?carrierId=${carrierId}` : "";
      const res = await fetch(`/api/plan-networks${params}`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
  });
  const [metalTier, setMetalTier] = useState(initialPlan?.metalTier || "");
  // ACA reporting facts â€” owned by the plan library, read-only everywhere else.
  const [fundingType, setFundingType] = useState(initialPlan?.fundingType || "fully_insured");
  const [minimumValue, setMinimumValue] = useState<string>(initialPlan?.minimumValue == null ? "derived" : initialPlan.minimumValue ? "yes" : "no");
  const [minimumEssentialCoverage, setMinimumEssentialCoverage] = useState<boolean>(initialPlan?.minimumEssentialCoverage !== false);
  const [description, setDescription] = useState(initialPlan?.description || "");
  const [effectiveDate, setEffectiveDate] = useState(initialPlan?.effectiveDate ? initialPlan.effectiveDate.split("T")[0] : "");
  const [endDate, setEndDate] = useState(initialPlan?.endDate ? initialPlan.endDate.split("T")[0] : "");
  const [availableStates, setAvailableStates] = useState<string[]>(initialPlan?.availableStates || []);
  const [rateStructureType, setRateStructureType] = useState(
    initialPlan?.rateStructureType || "tier_rates"
  );
  const [ageDetermination, setAgeDetermination] = useState(
    initialPlan?.ageDetermination || "plan_anniversary"
  );
  const [rates, setRates] = useState<PlanRate[]>(
    initialPlan?.rates && initialPlan.rates.length > 0
      ? dedupeAndSortRates(
          initialPlan.rates.map((r) => ({
            rateStructureType: r.rateStructureType,
            coverageTier: r.coverageTier,
            ageBand: r.ageBand,
            ageBandType: r.ageBandType,
            ratingAreaId: r.ratingAreaId,
            ratingAreaLabel: r.ratingAreaLabel,
            coverageTable: r.coverageTable,
            monthlyRate: r.monthlyRate,
          })),
        )
      : []
  );
  const [benefits, setBenefits] = useState<PlanBenefit[]>(
    initialPlan?.benefits || []
  );
  const [rawBenefits, setRawBenefits] = useState<RawBenefit[]>(
    initialPlan?.rawBenefits || []
  );
  // Templated plan types use the field-set editor (label + value boxes,
  // per-type fields â€” medical keeps the W&B set), backed by rawBenefits.
  // Types without a template keep the generic benefit-block editor below.
  const benefitTemplate = getBenefitFieldTemplate(type);
  const isTemplated = !!benefitTemplate;
  // Which rating area's age rows are shown when rates carry per-region pricing.
  const [selectedRatingArea, setSelectedRatingArea] = useState<string>("");

  const activeCarriers = carriers.filter((c) => c.isActive);

  // A "rating area" is a geographic pricing zone a carrier files different
  // rates for (e.g. California has 19 numbered areas) - a plan can save one
  // full set of rates per area instead of a single flat rate. This computes
  // which areas already have saved rows, for the "Add Region" picker and the
  // region-switcher dropdown rendered on the rate-editing cards below.
  //
  // `rates` is every row currently in the editor (any structure, any area) -
  // NOT yet saved to the server. `rateStructureType` is whichever "Rating
  // Method" option (Tier/Age/Age+Tier/Table) is selected right now, which
  // decides which ONE rate-editing card is showing below. We filter to that
  // structure specifically because switching the Rating Method dropdown
  // doesn't delete the other structures' rows until Save - so without this
  // filter, picking "Table Rates" could show Age Rates' regions instead.
  const ratingAreas = Array.from(
    new Map(
      rates
        .filter((r) => r.rateStructureType === rateStructureType && r.ratingAreaId != null)
        .map((r) => [String(r.ratingAreaId), r.ratingAreaLabel || `Area ${r.ratingAreaId}`]),
    ).entries(),
  )
    .map(([id, label]) => ({ id, label }))
    .sort((a, b) => Number(a.id) - Number(b.id));

  // No "all regions" mixed view: when per-region rows exist, the editor always
  // shows exactly one region, defaulting to the lowest-numbered rating area.
  useEffect(() => {
    if (ratingAreas.length === 0) {
      if (selectedRatingArea) setSelectedRatingArea("");
      return;
    }
    if (!ratingAreas.some((a) => a.id === selectedRatingArea)) {
      setSelectedRatingArea(ratingAreas[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratingAreas.map((a) => a.id).join(","), selectedRatingArea, rateStructureType]);

  // Tier rows shown in the editor: when the plan prices tiers per rating area
  // only the picked area's (up to 4) rows, same "one region visible at a
  // time" rule as the other area-capable cards.
  const isTierRowInView = (r: PlanRate) =>
    r.rateStructureType === "tier_rates" &&
    (ratingAreas.length === 0 || String(r.ratingAreaId ?? "") === selectedRatingArea);
  const tierRowsInView = rates.filter(isTierRowInView);

  const addTierRate = () => {
    const existingTiers = tierRowsInView.map(r => r.coverageTier);
    const nextTier = COVERAGE_TIERS.find(t => !existingTiers.includes(t.value));
    if (!nextTier) return;
    const area = ratingAreas.find((a) => a.id === selectedRatingArea);
    const areaFields = area ? { ratingAreaId: Number(area.id), ratingAreaLabel: area.label } : {};
    setRates([...rates, { rateStructureType: "tier_rates", coverageTier: nextTier.value, monthlyRate: "", ...areaFields }]);
  };

  const addAgeBandRates = (bandType: "five_year" | "all_ages" = "five_year") => {
    const bands = bandType === "all_ages" ? ALL_AGE_BANDS : DEFAULT_AGE_BANDS;
    const newRates = bands.map((band) => ({
      rateStructureType: "age_rates",
      ageBand: band,
      ageBandType: bandType,
      monthlyRate: "",
    }));
    setRates([...rates.filter(r => r.rateStructureType !== "age_rates"), ...newRates]);
  };

  // Called both to build the FIRST set of Age+Tier rows (no region yet) and,
  // since the band-size dropdown that calls this stays visible/switchable
  // after that (see the "Choose band size" Select below), to REBUILD when
  // the broker changes band size later. If one or more regions were already
  // added via "Add Region", rebuild EACH region's grid at the new band size
  // (cell values reset - the old and new bands don't line up - but the set
  // of regions itself is preserved) instead of collapsing them into one
  // region-less set.
  const addAgeTierRates = (bandType: "five_year" | "ten_year" = "five_year") => {
    const bands = AGE_BAND_PRESETS[bandType];
    const buildRowsFor = (areaFields: { ratingAreaId?: number; ratingAreaLabel?: string } = {}) =>
      bands.flatMap((band) =>
        COVERAGE_TIERS.map((t) => ({
          rateStructureType: "age_tier_rates",
          ageBand: band,
          ageBandType: bandType,
          coverageTier: t.value,
          monthlyRate: "",
          ...areaFields,
        })),
      );
    const existingRegions = Array.from(
      new Map(
        rates
          .filter((r) => r.rateStructureType === "age_tier_rates" && r.ratingAreaId != null)
          .map((r) => [String(r.ratingAreaId), { ratingAreaId: r.ratingAreaId as number, ratingAreaLabel: r.ratingAreaLabel as string }]),
      ).values(),
    );
    const newRates =
      existingRegions.length > 0 ? existingRegions.flatMap((region) => buildRowsFor(region)) : buildRowsFor();
    setRates([...rates.filter(r => r.rateStructureType !== "age_tier_rates"), ...newRates]);
  };

  const addTableRates = () => {
    const newRates = TABLE_RATE_LABELS.map((label) => ({
      rateStructureType: "table_rates",
      coverageTable: label,
      monthlyRate: "",
    }));
    setRates([...rates.filter(r => r.rateStructureType !== "table_rates"), ...newRates]);
  };

  // "Add Region" lets a plan save a SEPARATE full set of rates per rating
  // area instead of one flat set (e.g. a California plan can charge
  // different rates in Area 5 vs Area 19). This dialog's state: `regionState`
  // is the 2-letter state the broker types in; `stateAreas` is that state's
  // list of rating areas fetched from `/api/rating-areas?state=XX` (each has
  // an `id`, `areaNumber`, and display `label`); `regionAreaId` is which one
  // they picked from the dropdown. Shared by every area-capable structure
  // (Age Rates, Age Band + Tier Rates, Table Rates) - which one it builds
  // rows for is decided by `rateStructureType`, the currently-selected
  // "Rating Method" option, since that's which rate-editing card is showing.
  const [addRegionOpen, setAddRegionOpen] = useState(false);
  const [regionState, setRegionState] = useState("");
  const [regionAreaId, setRegionAreaId] = useState<string>("");
  const { data: stateAreas = [], isFetching: isFetchingStateAreas } = useQuery<any[]>({
    queryKey: ["/api/rating-areas", regionState.toUpperCase()],
    queryFn: async () => {
      const res = await fetch(`/api/rating-areas?state=${regionState.toUpperCase()}`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: /^[A-Za-z]{2}$/.test(regionState),
  });

  // States this plan's EXISTING clients are actually in (from their
  // client_plans link's own rating location, falling back to the client's
  // address) - so adding a region doesn't require going to check each
  // client's profile first. Only meaningful for an already-saved plan
  // (`planId` set) - a brand-new plan has no client links yet.
  const { data: linkedStatesData } = useQuery<{ states: string[] }>({
    queryKey: ["/api/plans", planId, "linked-states"],
    queryFn: async () => {
      const res = await fetch(`/api/plans/${planId}/linked-states`, { credentials: "include" });
      if (!res.ok) return { states: [] };
      return res.json();
    },
    enabled: planId != null && addRegionOpen,
  });
  const linkedStates = linkedStatesData?.states ?? [];
  // Prefill the state field only when every linked client shares ONE state -
  // an unambiguous default. 0 or 2+ states are left blank (nothing to
  // safely guess), but linkedStates still renders as quick-pick chips below
  // either way.
  useEffect(() => {
    if (addRegionOpen && !regionState && linkedStates.length === 1) {
      setRegionState(linkedStates[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addRegionOpen, linkedStates.join(",")]);

  // Builds one full set of blank rate rows for the picked area, in whichever
  // shape the currently-selected Rating Method needs: Age Rates = one row
  // per age band; Age Band + Tier Rates = one row per (age band, coverage
  // tier) pair; Table Rates = one row per the 8 fixed coverage-combination
  // labels (see TABLE_RATE_LABELS). Appends to `rates` (the editor's
  // in-memory row list) rather than replacing it, since other areas' rows
  // for this same structure must stay untouched.
  const addRegionRates = () => {
    const area = stateAreas.find((a: any) => String(a.id) === regionAreaId);
    if (!area) return;
    if (rates.some((r) => r.rateStructureType === rateStructureType && String(r.ratingAreaId) === String(area.id))) {
      setSelectedRatingArea(String(area.id));
      setAddRegionOpen(false);
      return;
    }
    const label = area.label && !/^AREA \d+$/i.test(area.label)
      ? area.label
      : `${regionState.toUpperCase()}-${area.areaNumber ?? area.id}`;
    const areaFields = { ratingAreaId: Number(area.id), ratingAreaLabel: label };

    let newRates: PlanRate[];
    if (rateStructureType === "age_tier_rates") {
      // Match whichever age-band size (5-year or 10-year) this plan's other
      // Age+Tier regions already use, so every region lines up on the same
      // bands; defaults to 5-year if this is the plan's first region.
      const existing = rates.find((r) => r.rateStructureType === "age_tier_rates");
      const bandType = (existing?.ageBandType as "five_year" | "ten_year") || "five_year";
      newRates = AGE_BAND_PRESETS[bandType].flatMap((band) =>
        COVERAGE_TIERS.map((t) => ({
          rateStructureType: "age_tier_rates",
          ageBand: band,
          ageBandType: bandType,
          coverageTier: t.value,
          monthlyRate: "",
          ...areaFields,
        })),
      );
    } else if (rateStructureType === "tier_rates") {
      newRates = COVERAGE_TIERS.map((t) => ({
        rateStructureType: "tier_rates",
        coverageTier: t.value,
        monthlyRate: "",
        ...areaFields,
      }));
    } else if (rateStructureType === "table_rates") {
      newRates = TABLE_RATE_LABELS.map((tableLabel) => ({
        rateStructureType: "table_rates",
        coverageTable: tableLabel,
        monthlyRate: "",
        ...areaFields,
      }));
    } else {
      // Age Rates (the default/original case): match the age-band size this
      // plan's other regions already use.
      const existingAge = rates.find((r) => r.rateStructureType === "age_rates");
      const bandType = (existingAge?.ageBandType as "five_year" | "all_ages") || "five_year";
      const bands = bandType === "all_ages" ? ALL_AGE_BANDS : DEFAULT_AGE_BANDS;
      newRates = bands.map((band) => ({
        rateStructureType: "age_rates",
        ageBand: band,
        ageBandType: bandType,
        monthlyRate: "",
        ...areaFields,
      }));
    }
    setRates([...rates, ...newRates]);
    setSelectedRatingArea(String(area.id));
    setAddRegionOpen(false);
    setRegionAreaId("");
  };

  // Deletes every row of the CURRENTLY SELECTED structure for one area (the
  // trash-icon button next to the region dropdown, guarded by a confirm
  // dialog further down) - other areas' rows, and every OTHER structure's
  // rows, are untouched.
  const removeRegionRates = (areaId: string) => {
    setRates(rates.filter((r) => !(r.rateStructureType === rateStructureType && String(r.ratingAreaId) === areaId)));
    setSelectedRatingArea("");
  };

  // CSV rate import: parse â†’ preview â†’ load into the editor's rate rows.
  // Saving then publishes through the normal versioned path â€” the import
  // itself never writes to the server.
  const [csvImportOpen, setCsvImportOpen] = useState(false);

  // --- Rate cadence -------------------------------------------------------
  // `rates` holds the rate set for the CURRENTLY SELECTED period, so every
  // existing editor affordance (age bands, regions, Import CSV, tier rows)
  // keeps working untouched â€” it just operates on one period at a time.
  // The other periods live in periodRates and are swapped in on tab change.
  // cadence "annual" = exactly today's behaviour: one set, no tabs.
  const [cadence, setCadence] = useState<RateCadence>("annual");
  const [cadenceYear, setCadenceYear] = useState<number>(() => {
    const y = Number(String(initialPlan?.effectiveDate ?? "").slice(0, 4));
    return Number.isFinite(y) && y > 2000 ? y : new Date().getFullYear();
  });
  const [activePeriod, setActivePeriod] = useState(1);
  const [periodRates, setPeriodRates] = useState<Record<number, PlanRate[]>>({});

  // Editing a plan that already has a cadence year: load it so the tabs show
  // what was published rather than defaulting back to annual.
  const { data: existingRateYears } = useQuery<any[]>({
    queryKey: ["/api/plans", planId, "rate-years"],
    queryFn: async () => {
      const res = await fetch(`/api/plans/${planId}/rate-years`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch rate years");
      return res.json();
    },
    enabled: !!planId,
  });

  const loadedYearRef = useRef<number | null>(null);
  useEffect(() => {
    const year = existingRateYears?.[0];
    if (!year || year.cadence === "annual" || loadedYearRef.current === year.id) return;
    loadedYearRef.current = year.id;
    (async () => {
      const res = await fetch(`/api/plans/${planId}/rate-years/${year.year}`, { credentials: "include" });
      if (!res.ok) return;
      const full = await res.json();
      const byPeriod: Record<number, PlanRate[]> = {};
      for (const p of full.periods ?? []) {
        byPeriod[p.periodIndex] = (p.rows ?? []).map((r: any) => ({
          rateStructureType: r.rateStructureType,
          coverageTier: r.coverageTier,
          ageBand: r.ageBand,
          ageBandType: r.ageBandType,
          ratingAreaId: r.ratingAreaId,
          ratingAreaLabel: r.ratingAreaLabel,
          coverageTable: r.coverageTable,
          monthlyRate: String(r.monthlyRate ?? ""),
        }));
      }
      setCadence(full.cadence);
      setCadenceYear(full.year);
      setActivePeriod(1);
      setPeriodRates(byPeriod);
      setRates(byPeriod[1] ?? []);
    })();
  }, [existingRateYears, planId]);

  const switchPeriod = (next: number) => {
    if (next === activePeriod) return;
    setPeriodRates((prev) => ({ ...prev, [activePeriod]: rates }));
    setRates(periodRates[next] ?? []);
    setActivePeriod(next);
  };

  const changeCadence = (next: RateCadence) => {
    // Going narrower drops periods that no longer exist; going wider leaves
    // the new ones empty (and the save is blocked until they're filled).
    if (next === cadence) return;
    const keep = periodsPerCadence(next);
    if (activePeriod > keep) switchPeriod(1);
    setPeriodRates((prev) => {
      const out: Record<number, PlanRate[]> = {};
      for (let p = 1; p <= keep; p++) if (prev[p]) out[p] = prev[p];
      return out;
    });
    setCadence(next);
  };
  const [csvText, setCsvText] = useState("");
  const csvParsed = useMemo(() => (csvText.trim() ? parseRateCsv(csvText) : null), [csvText]);
  // Exactly what Load would put in the editor â€” the preview and the toast
  // report from this, so they count rows KEPT, not rows read (DEV-324).
  const csvPlan = useMemo(
    () => (csvParsed && csvParsed.rows.length > 0 ? planCsvImport(csvParsed.rows, rates) : null),
    [csvParsed, rates],
  );
  const csvAreas = csvPlan?.areas ?? [];
  const csvStructureNoun =
    csvPlan?.structure === "age_tier_rates"
      ? "age + tier"
      : csvPlan?.structure === "table_rates"
        ? "table"
        : csvPlan?.structure === "tier_rates"
          ? "tier"
          : "age-band";
  // A plan already priced per region can't take an area-less import of the
  // same structure â€” it would collapse every area into one set.
  const csvNeedsAreaColumn = csvPlan?.needsAreaColumn ?? false;
  const csvDroppedRows = csvPlan ? csvPlan.fileRows - csvPlan.keptRows : 0;

  const downloadCsvTemplate = (layout: "long" | "wide" | "table" = "long") => {
    const template =
      layout === "wide" ? RATE_CSV_TEMPLATE_WIDE : layout === "table" ? RATE_CSV_TEMPLATE_TABLE : RATE_CSV_TEMPLATE;
    const blob = new Blob([template], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download =
      layout === "wide" ? "rate-import-template-wide.csv" : layout === "table" ? "rate-import-template-table.csv" : "rate-import-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const applyCsvImport = () => {
    if (!csvPlan || csvPlan.keptRows === 0 || csvNeedsAreaColumn) return;
    setRates(csvPlan.rates);
    setRateStructureType(csvPlan.structure);
    if (csvPlan.areas.length > 0) setSelectedRatingArea(String(csvPlan.areas[0]));
    setCsvImportOpen(false);
    setCsvText("");
    const ignored = (csvParsed?.warnings.length ?? 0) > 0 ? " Some columns in the file were ignored (see the import preview)." : "";
    toast({
      title: `${csvPlan.keptRows} rates loaded into the editor`,
      description:
        (csvDroppedRows > 0
          ? `${csvDroppedRows} of ${csvPlan.fileRows} file rows were duplicates or area-less rows and were not kept. `
          : "") + `Review the rows, then Save â€” that publishes them as a rate version.${ignored}`,
    });
  };

  const updateRate = (index: number, field: string, value: string) => {
    const updated = [...rates];
    (updated[index] as any)[field] = value;
    setRates(updated);
  };

  const removeRate = (index: number) => {
    setRates(rates.filter((_, i) => i !== index));
  };

  const addBenefit = () => {
    setBenefits([...benefits, { benefitType: "medical" }]);
  };

  const updateBenefit = (index: number, field: string, value: string) => {
    const updated = [...benefits];
    (updated[index] as any)[field] = value;
    setBenefits(updated);
  };

  const updateBenefitFlag = (index: number, field: string, value: boolean) => {
    const updated = [...benefits];
    (updated[index] as any)[field] = value;
    setBenefits(updated);
  };

  const removeBenefit = (index: number) => {
    setBenefits(benefits.filter((_, i) => i !== index));
  };

  /**
   * All periods, current one included, as usable rate rows. Filtered to the
   * active Rating Method - `rates`/`periodRates` can hold rows from a
   * Rating Method that isn't selected anymore.
   */
  const allPeriodRates = (): Record<number, PlanRate[]> => {
    const merged = { ...periodRates, [activePeriod]: rates };
    const out: Record<number, PlanRate[]> = {};
    for (let p = 1; p <= periodsPerCadence(cadence); p++) {
      out[p] = (merged[p] ?? []).filter(
        (r) => r.rateStructureType === rateStructureType && r.monthlyRate && parseFloat(r.monthlyRate) > 0,
      );
    }
    return out;
  };

  /** Periods still empty â€” they stay unpublished; shown so nobody forgets them. */
  const emptyPeriods = (): number[] => {
    if (cadence === "annual") return [];
    const byPeriod = allPeriodRates();
    const out: number[] = [];
    for (let p = 1; p <= periodsPerCadence(cadence); p++) if (byPeriod[p].length === 0) out.push(p);
    return out;
  };

  /**
   * Multi-set years publish through /rate-years. Annual keeps the existing
   * single-version `rates` path so nothing changes for plans that don't use
   * cadence. Only periods that carry rates are sent â€” the others stay
   * unpublished until they are filled in (a carrier files Q1 first and the
   * rest as they come), and the server leaves them untouched.
   */
  const buildRateYearPayload = () => {
    if (cadence === "annual") return undefined;
    const byPeriod = allPeriodRates();
    const filled = Array.from({ length: periodsPerCadence(cadence) }, (_, i) => i + 1).filter(
      (p) => byPeriod[p].length > 0,
    );
    if (filled.length === 0) return undefined;
    return {
      year: cadenceYear,
      cadence,
      periods: filled.map((p) => ({
        periodIndex: p,
        rows: byPeriod[p].map((r) => ({
          rateStructureType: r.rateStructureType,
          coverageTier: r.coverageTier ?? null,
          ageBand: r.ageBand ?? null,
          ageBandType: r.ageBandType ?? null,
          ratingAreaId: r.ratingAreaId ?? null,
          ratingAreaLabel: r.ratingAreaLabel ?? null,
          coverageTable: r.coverageTable ?? null,
          monthlyRate: r.monthlyRate,
          origin: "manual",
        })),
      })),
    };
  };

  // Editing the same effective period replaces that version's rows entirely
  // (rateVersioning.ts publishRateVersion) - switching Rating Method and
  // saving deletes the plan's current rates for this period, unrecoverably.
  // Confirm first.
  const originalStructureType = initialPlan?.rateStructureType || "tier_rates";
  const structureChangedOnExistingPlan = Boolean(initialPlan) && rateStructureType !== originalStructureType;
  const [confirmStructureChangeOpen, setConfirmStructureChangeOpen] = useState(false);
  // Feeds the "Remove Region" confirm dialog further below (how many rows
  // and which area's label to show) - scoped to the currently-selected
  // Rating Method's rows, same reasoning as ratingAreas/addRegionRates above.
  const [confirmRemoveRegionOpen, setConfirmRemoveRegionOpen] = useState(false);
  const selectedRegionRowCount = rates.filter(
    (r) => r.rateStructureType === rateStructureType && String(r.ratingAreaId) === selectedRatingArea,
  ).length;
  const selectedRegionLabel = ratingAreas.find((a) => a.id === selectedRatingArea)?.label ?? selectedRatingArea;

  // The region-picker dropdown + trash button + "Add Region" button shown in
  // each area-capable rate card's header. One shared block, since it's
  // identical for Age Rates, Age Band + Tier Rates, and Table Rates - only
  // WHICH structure's rows/areas it reads (ratingAreas, selectedRatingArea)
  // changes, and those already track the currently-selected Rating Method.
  const regionControls = (
    <>
      {ratingAreas.length > 0 && (
        <>
          <MapPin className="h-3.5 w-3.5 text-gray-400" />
          <Select value={selectedRatingArea} onValueChange={setSelectedRatingArea}>
            <SelectTrigger className="w-40 h-8 text-sm" data-testid="rating-area-select">
              <SelectValue placeholder="All regions" />
            </SelectTrigger>
            <SelectContent>
              {ratingAreas.map((area) => (
                <SelectItem key={area.id} value={area.id}>{area.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedRatingArea && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 px-2"
              title="Remove this region's rate collection"
              onClick={() => setConfirmRemoveRegionOpen(true)}
            >
              <Trash2 className="h-3.5 w-3.5 text-red-400" />
            </Button>
          )}
        </>
      )}
      <Button type="button" variant="outline" size="sm" onClick={() => setAddRegionOpen(true)}>
        <Plus className="h-3.5 w-3.5 mr-1" /> Add Region
      </Button>
    </>
  );

  const handleSubmit = () => {
    if (structureChangedOnExistingPlan) {
      setConfirmStructureChangeOpen(true);
      return;
    }
    submitSave();
  };

  const submitSave = () => {
    // Only submit rows matching the active Rating Method - `rates` can hold
    // leftover rows from a method the user isn't on anymore.
    const validRates = rates.filter(
      (r) => r.rateStructureType === rateStructureType && r.monthlyRate && parseFloat(r.monthlyRate) > 0,
    );
    const validBenefits = benefits.filter((b) => b.benefitType);

    onSave({
      name,
      type,
      carrierId,
      planCode,
      networkType: networkType || undefined,
      // Edit mode may clear the network â€” PATCH takes null; POST omits.
      network: network.trim() || (planId ? null : undefined),
      metalTier: metalTier || undefined,
      fundingType: fundingType || undefined,
      minimumValue: minimumValue === "derived" ? null : minimumValue === "yes",
      minimumEssentialCoverage,
      description: description || undefined,
      rateStructureType: rateStructureType || undefined,
      ageDetermination: ageDetermination || undefined,
      effectiveDate: effectiveDate || undefined,
      endDate: endDate || undefined,
      availableStates: availableStates.length > 0 ? availableStates : undefined,
      rawBenefits:
        rawBenefits.length > 0
          ? rawBenefits.map((r) => ({ ...r, value: r.value.trim() })).filter((r) => r.value)
          : undefined,
      // Mutually exclusive with rateYear below: publishing a single version AND
      // a rate year into the same calendar year would trip the managed-year
      // guard (UNMANAGED_VERSIONS_IN_YEAR) on the server.
      rates: cadence === "annual" && validRates.length > 0 ? validRates : undefined,
      benefits: validBenefits.length > 0 ? validBenefits : undefined,
      // Create mode: forms picked before the plan exists â€” attached by the
      // caller right after creation.
      formIds: !planId && pendingForms.length > 0 ? pendingForms.map((f) => f.id) : undefined,
      // Multi-set year: the caller publishes every period as one rate year.
      // Annual stays on the plain `rates` path above, unchanged.
      rateYear: buildRateYearPayload(),
    });
  };

  // Periods publish independently; empty ones are flagged on their tab and
  // simply have no rate until they are added (see buildRateYearPayload) - so
  // save is not blocked on missingPeriods, only on the base fields below.
  const missingPeriods = emptyPeriods();
  const isValid = name && type && carrierId && planCode;
  // Spelled out below the Save button instead of a silently disabled one.
  // Network type (DEV-388) is listed for review readiness but never blocks saving a draft.
  const missingFields = planBuilderMissingFields({ name, type, carrierId, planCode, networkType });
  const missingReasons = [...missingFields.blocking, ...missingFields.advisory];

  return (
    <div className="space-y-6">
      <Tabs defaultValue="details">
        <TabsList className={`grid w-full ${isSuperadminVariant ? "grid-cols-4" : "grid-cols-3"}`}>
          <TabsTrigger value="details">Plan Details</TabsTrigger>
          <TabsTrigger value="rates">Rates</TabsTrigger>
          <TabsTrigger value="benefits">Benefits</TabsTrigger>
          {isSuperadminVariant && <TabsTrigger value="forms">Forms</TabsTrigger>}
        </TabsList>

        <TabsContent value="details" className="space-y-4 pt-4">
          {planId && <SystemPlanIdBadge planId={planId} />}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Plan Name *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Blue Shield Gold PPO" />
            </div>
            <div>
              <Label>Plan Code (carrier) *</Label>
              <Input value={planCode} onChange={(e) => setPlanCode(e.target.value)} placeholder="e.g., BSC-GOLD-PPO-2025" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Plan Type *</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLAN_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Carrier *</Label>
              <Select value={carrierId?.toString() || ""} onValueChange={(v) => setCarrierId(parseInt(v))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select carrier" />
                </SelectTrigger>
                <SelectContent>
                  {activeCarriers.map((c) => (
                    <SelectItem key={c.id} value={c.id.toString()}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* Network type: medical, dental and vision (review readiness, DEV-388); the ACA fields are medical-only. */}
          {planTypeNeedsNetworkType(type) && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Network Type</Label>
                <Select value={networkType} onValueChange={setNetworkType}>
                  <SelectTrigger data-testid="plan-network-type">
                    <SelectValue placeholder="Select network type" />
                  </SelectTrigger>
                  <SelectContent>
                    {NETWORK_TYPES.map((n) => (
                      <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {(type === "health" || type === "medical") && (
              <>
              <div>
                <Label>Metal Tier</Label>
                <Select value={metalTier} onValueChange={setMetalTier}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select metal tier" />
                  </SelectTrigger>
                  <SelectContent>
                    {METAL_TIERS.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Funding (ACA reporting)</Label>
                <Select value={fundingType} onValueChange={setFundingType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fully_insured">Fully insured</SelectItem>
                    <SelectItem value="self_insured">Self-insured</SelectItem>
                    <SelectItem value="level_funded">Level-funded (self-insured for ACA)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Self/level-funded plans require 1095-C Part III covered individuals.</p>
              </div>
              <div>
                <Label>Minimum Value (ACA)</Label>
                <Select value={minimumValue} onValueChange={setMinimumValue}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="derived">Derived from metal tier (any tier = MV)</SelectItem>
                    <SelectItem value="yes">Provides minimum value (AV at least 60%)</SelectItem>
                    <SelectItem value="no">Does not provide minimum value</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Minimum Essential Coverage (ACA)</Label>
                <Select value={minimumEssentialCoverage ? "yes" : "no"} onValueChange={(v) => setMinimumEssentialCoverage(v === "yes")}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Is MEC</SelectItem>
                    <SelectItem value="no">Not MEC (excepted benefit)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              </>
              )}
            </div>
          )}
          <div>
            <Label>Network</Label>
            <Input
              value={network}
              onChange={(e) => setNetwork(e.target.value)}
              placeholder='e.g., Select HMO, California Care HMO'
              list="plan-network-suggestions"
            />
            <datalist id="plan-network-suggestions">
              {networkSuggestions.map((n) => (
                <option key={n} value={n} />
              ))}
            </datalist>
            <p className="mt-1 text-xs text-muted-foreground">
              The carrier's named provider network â€” distinguishes same-named plans on different
              networks. Suggestions come from networks already used for this carrier.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Effective Date</Label>
              <Input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
            </div>
            <div>
              <Label>End Date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Available States</Label>
            <div className="mt-1.5 border rounded-md p-3 max-h-40 overflow-y-auto">
              <div className="flex flex-wrap gap-1.5">
                {US_STATES.map((st) => (
                  <button
                    key={st}
                    type="button"
                    onClick={() => setAvailableStates((prev) => prev.includes(st) ? prev.filter((s) => s !== st) : [...prev, st])}
                    className={`px-2 py-0.5 text-xs rounded border transition-colors ${
                      availableStates.includes(st)
                        ? "bg-[#1737A2] text-white border-[#1737A2]"
                        : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
                    }`}
                  >
                    {st}
                  </button>
                ))}
              </div>
              {availableStates.length > 0 && (
                <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                  <span>{availableStates.length} state{availableStates.length !== 1 ? "s" : ""} selected</span>
                  <button type="button" className="text-red-500 hover:underline" onClick={() => setAvailableStates([])}>Clear all</button>
                </div>
              )}
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Plan description, coverage highlights, or internal notes..."
              rows={3}
            />
          </div>
        </TabsContent>

        <TabsContent value="rates" className="space-y-4 pt-4">
          <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-200">
            Saving rates publishes a version effective from the plan's{" "}
            <span className="font-medium">effective date</span> ({effectiveDate || "not set"}).
            Historic rates are never overwritten â€” to change rates mid-year, set a new effective date
            and save; the prior version is kept for that period (ACA, renewals, historical reports).
            {rateImpact && (rateImpact.affectedEnrollments > 0 || rateImpact.affectedClients > 0) && (
              <span className="mt-1 block font-medium">
                A new version reaches {rateImpact.affectedEnrollments} active enrollment
                {rateImpact.affectedEnrollments !== 1 ? "s" : ""} across {rateImpact.affectedClients} employer
                {rateImpact.affectedClients !== 1 ? "s" : ""} â€” each at its own plan-year renewal (in-force plan years
                keep their locked version).
              </span>
            )}
          </div>
          <div className="flex items-end justify-between gap-2">
            <div className="flex items-end gap-2">
              <div>
                <Label>Rating Method</Label>
                <Select value={rateStructureType} onValueChange={setRateStructureType}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RATE_STRUCTURE_TYPES.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Age Determination</Label>
                <Select value={ageDetermination} onValueChange={setAgeDetermination}>
                  <SelectTrigger className="w-56">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AGE_DETERMINATION_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Cadence is the ONLY thing that changes here: it splits the
                  same editor below into 1, 2 or 4 rate sets. Everything else â€”
                  the grid, regions, Import CSV â€” works exactly as before, per
                  period. */}
              <div>
                <Label>Rate sets / year</Label>
                <Select value={cadence} onValueChange={(v) => changeCadence(v as RateCadence)}>
                  <SelectTrigger className="w-44">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RATE_CADENCES.map((c) => (
                      <SelectItem key={c} value={c}>{CADENCE_LABELS[c]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {cadence !== "annual" && (
                <div>
                  <Label>Rate year</Label>
                  <Input
                    type="number"
                    className="w-24"
                    value={cadenceYear}
                    onChange={(e) => setCadenceYear(Number(e.target.value))}
                  />
                </div>
              )}
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => setCsvImportOpen(true)}>
              <Upload className="h-3.5 w-3.5 mr-1" /> Import CSV
            </Button>
          </div>

          {cadence !== "annual" && (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-1">
                {Array.from({ length: periodsPerCadence(cadence) }, (_, i) => {
                  const p = i + 1;
                  const count = (p === activePeriod ? rates : periodRates[p] ?? []).filter(
                    (r) => r.monthlyRate && parseFloat(r.monthlyRate) > 0,
                  ).length;
                  return (
                    <Button
                      key={p}
                      type="button"
                      size="sm"
                      variant={p === activePeriod ? "default" : "outline"}
                      onClick={() => switchPeriod(p)}
                    >
                      {periodLabel(cadenceYear, cadence, p)}
                      <Badge
                        variant={count === 0 ? "outline" : "secondary"}
                        className="ml-2 text-[10px]"
                      >
                        {count === 0 ? "no rates" : count}
                      </Badge>
                    </Button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                Editing <span className="font-medium">{periodLabel(cadenceYear, cadence, activePeriod)}</span>
                {" â€” "}applies to plan years beginning{" "}
                {periodWindow(cadenceYear, cadence, activePeriod).effectiveStart} up to (not including){" "}
                {periodWindow(cadenceYear, cadence, activePeriod).effectiveEnd}. Periods can be added one
                at a time: import a CSV into this tab and save, then come back for the next one.
                {missingPeriods.length > 0 && (
                  <>
                    {" "}Until they have rates, plan years starting in{" "}
                    <span className="font-medium">
                      {missingPeriods.map((p) => periodLabel(cadenceYear, cadence, p)).join(", ")}
                    </span>{" "}
                    have no rate and cannot be quoted or billed.
                  </>
                )}
              </p>
            </div>
          )}

          {rateStructureType === "tier_rates" && (
            <Card>
              <CardHeader className="py-3 px-4">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-sm font-medium">Tier Rates</CardTitle>
                  <div className="flex items-center gap-2">
                    {/* At most one row per coverage tier â€” per rating area
                        when the plan prices tiers by area (DEV-324). */}
                    <Button type="button" variant="outline" size="sm" onClick={addTierRate} disabled={tierRowsInView.length >= COVERAGE_TIERS.length}>
                      <Plus className="h-3.5 w-3.5 mr-1" /> Add Tier
                    </Button>
                    {regionControls}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                {tierRowsInView.length === 0 ? (
                  <p className="text-sm text-gray-400 py-4 text-center">No rates added. Click "Add Tier" to add coverage tier rates.</p>
                ) : (
                  <div className="space-y-2">
                    {rates.map((rate, index) => {
                      if (!isTierRowInView(rate)) return null;
                      return (
                        <div key={index} className="flex items-center gap-3" data-testid={`tier-rate-row-${rate.coverageTier ?? "unset"}`}>
                          <Select
                            value={rate.coverageTier || ""}
                            onValueChange={(v) => updateRate(index, "coverageTier", v)}
                          >
                            <SelectTrigger className="w-48">
                              <SelectValue placeholder="Select tier" />
                            </SelectTrigger>
                            <SelectContent>
                              {COVERAGE_TIERS.map((t) => (
                                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <div className="flex items-center gap-1 flex-1">
                            <DollarSign className="h-4 w-4 text-gray-400" />
                            <Input
                              type="number"
                              step="0.01"
                              value={rate.monthlyRate}
                              onChange={(e) => updateRate(index, "monthlyRate", e.target.value)}
                              placeholder="Monthly rate"
                            />
                          </div>
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeRate(index)}>
                            <X className="h-4 w-4 text-gray-400" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {rateStructureType === "age_rates" && (
            <Card>
              <CardHeader className="py-3 px-4">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-sm font-medium">Age Band Rates</CardTitle>
                  <div className="flex items-center gap-2">
                    {rates.filter(r => r.rateStructureType === "age_rates").length === 0 && (
                      <>
                        <Button type="button" variant="outline" size="sm" onClick={() => addAgeBandRates("five_year")}>
                          <Plus className="h-3.5 w-3.5 mr-1" /> Load Age Bands
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => addAgeBandRates("all_ages")}>
                          <Plus className="h-3.5 w-3.5 mr-1" /> Per single age
                        </Button>
                      </>
                    )}
                    {regionControls}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                {rates.filter(r => r.rateStructureType === "age_rates").length === 0 ? (
                  <p className="text-sm text-gray-400 py-4 text-center">Click "Load Age Bands" (wide brackets) or "Per single age" (one row per age, the granularity carriers file at) to add rows.</p>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {rates.map((rate, index) => {
                      if (rate.rateStructureType !== "age_rates") return null;
                      // When per-region rows exist, show only the picked region.
                      if (
                        ratingAreas.length > 0 &&
                        selectedRatingArea &&
                        String(rate.ratingAreaId) !== selectedRatingArea
                      ) return null;
                      return (
                        <div key={index} className="flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-500 w-16">{rate.ageBand}</span>
                          <div className="flex items-center gap-1 flex-1">
                            <DollarSign className="h-3.5 w-3.5 text-gray-400" />
                            <Input
                              type="number"
                              step="0.01"
                              value={rate.monthlyRate}
                              onChange={(e) => updateRate(index, "monthlyRate", e.target.value)}
                              placeholder="Rate"
                              className="h-8 text-sm"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {rateStructureType === "age_tier_rates" && (
            <Card>
              <CardHeader className="py-3 px-4">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-sm font-medium">Age Band + Tier Rates</CardTitle>
                  {/* Always visible, not just before the first load - switching
                      band size rebuilds the grid (existing values can't carry
                      over across different band boundaries), same behavior
                      as the equivalent dropdown on the broker's Rates tab. */}
                  <div className="flex items-center gap-2">
                    <Select
                      value={(rates.find((r) => r.rateStructureType === "age_tier_rates")?.ageBandType as string) || ""}
                      onValueChange={(v) => addAgeTierRates(v as "five_year" | "ten_year")}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Choose band size" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="five_year">5 Year Age Bands</SelectItem>
                        <SelectItem value="ten_year">10 Year Age Bands</SelectItem>
                      </SelectContent>
                    </Select>
                    {regionControls}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                {rates.filter(r => r.rateStructureType === "age_tier_rates").length === 0 ? (
                  <p className="text-sm text-gray-400 py-4 text-center">Choose a band size above to add one row per age band Ã— coverage tier.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr>
                          <th className="text-left font-medium text-gray-500 pb-2 pr-3">Age Band</th>
                          {COVERAGE_TIERS.map((t) => (
                            <th key={t.value} className="text-center font-medium text-gray-500 pb-2 px-2">{t.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {Array.from(
                          new Set(
                            rates
                              // When this plan has multiple regions (added via
                              // "Add Region"), show only the currently-picked
                              // one's bands/rates - same "one region visible at
                              // a time" rule the Age Rates card above uses.
                              .filter(
                                (r) =>
                                  r.rateStructureType === "age_tier_rates" &&
                                  (ratingAreas.length === 0 || String(r.ratingAreaId ?? "") === selectedRatingArea),
                              )
                              .map(r => r.ageBand as string),
                          ),
                        )
                          .sort((a, b) => ageBandStart(a) - ageBandStart(b))
                          .map((band) => (
                            <tr key={band}>
                              <td className="text-xs font-medium text-gray-500 py-1 pr-3">{band}</td>
                              {COVERAGE_TIERS.map((t) => {
                                const index = rates.findIndex(
                                  (r) =>
                                    r.rateStructureType === "age_tier_rates" &&
                                    r.ageBand === band &&
                                    r.coverageTier === t.value &&
                                    (ratingAreas.length === 0 || String(r.ratingAreaId ?? "") === selectedRatingArea),
                                );
                                if (index === -1) return <td key={t.value} />;
                                return (
                                  <td key={t.value} className="px-2 py-1">
                                    <Input
                                      type="number"
                                      step="0.01"
                                      value={rates[index].monthlyRate}
                                      onChange={(e) => updateRate(index, "monthlyRate", e.target.value)}
                                      placeholder="0.00"
                                      className="h-8 text-sm w-24 mx-auto"
                                    />
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {rateStructureType === "table_rates" && (
            <Card>
              <CardHeader className="py-3 px-4">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-sm font-medium">Table Rates</CardTitle>
                  <div className="flex items-center gap-2">
                    {rates.filter(r => r.rateStructureType === "table_rates").length === 0 && (
                      <Button type="button" variant="outline" size="sm" onClick={addTableRates}>
                        <Plus className="h-3.5 w-3.5 mr-1" /> Load Coverage Combinations
                      </Button>
                    )}
                    {regionControls}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                {rates.filter(r => r.rateStructureType === "table_rates").length === 0 ? (
                  <p className="text-sm text-gray-400 py-4 text-center">Click "Load Coverage Combinations" to add the 8 fixed rate rows.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {TABLE_RATE_LABELS.map((label) => {
                      // When this plan has multiple regions (added via "Add
                      // Region"), show only the currently-picked one's rates -
                      // same "one region visible at a time" rule the Age Rates
                      // card uses.
                      const index = rates.findIndex(
                        (r) =>
                          r.rateStructureType === "table_rates" &&
                          r.coverageTable === label &&
                          (ratingAreas.length === 0 || String(r.ratingAreaId ?? "") === selectedRatingArea),
                      );
                      if (index === -1) return null;
                      return (
                        <div key={label} className="space-y-1">
                          <Label className="text-xs font-medium text-gray-500">{label}</Label>
                          <div className="flex items-center gap-1">
                            <DollarSign className="h-3.5 w-3.5 text-gray-400" />
                            <Input
                              type="number"
                              step="0.01"
                              value={rates[index].monthlyRate}
                              onChange={(e) => updateRate(index, "monthlyRate", e.target.value)}
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <Dialog open={csvImportOpen} onOpenChange={(open) => { setCsvImportOpen(open); if (!open) setCsvText(""); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Import rates from CSV</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                One plan per file. Columns: <code>rating_area</code> (optional),{" "}
                <code>age_band</code>, <code>coverage_tier</code>, and/or <code>coverage_table</code>
                (both age and tier together = Age Band + Tier Rates; coverage_table alone = Table
                Rates), and <code>monthly_rate</code> ("$1,234.56" is fine). The wide layout ("Age,
                Rating Area 1, Rating Area 2, â€¦" â€” Kaiser-style) is also recognized, as are header
                names like "area", "age", "rate", "premium", "table" or "label".{" "}
                Blank templates (no rates â€” fill and upload):{" "}
                <button type="button" className="text-blue-600 hover:underline" onClick={() => downloadCsvTemplate("wide")}>
                  age Ã— rating-area grid
                </button>
                {" Â· "}
                <button type="button" className="text-blue-600 hover:underline" onClick={() => downloadCsvTemplate("long")}>
                  one row per rate
                </button>
                {" Â· "}
                <button type="button" className="text-blue-600 hover:underline" onClick={() => downloadCsvTemplate("table")}>
                  table rates
                </button>
              </p>
              <Input
                type="file"
                accept=".csv,text/csv,text/plain"
                className="h-9"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => setCsvText(String(reader.result ?? ""));
                  reader.readAsText(file);
                }}
              />
              <Textarea
                rows={7}
                className="font-mono text-xs"
                placeholder={"â€¦or paste CSV here\nrating_area,age_band,monthly_rate\n1,0-14,487.62"}
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
              />
              {csvParsed && (
                <div className="rounded-md border p-2.5 text-xs space-y-1">
                  {csvPlan && (
                    <p data-testid="csv-import-summary">
                      <span className="font-medium">{csvPlan.keptRows}</span>{" "}
                      {csvStructureNoun} rate
                      {csvPlan.keptRows === 1 ? "" : "s"}
                      {csvAreas.length > 0 && (
                        <> across areas {csvAreas.join(", ")}</>
                      )}
                      {" "}â€” replaces the{" "}
                      {csvAreas.length > 0 ? `listed areas' ${csvStructureNoun} rows` : `${csvStructureNoun} rates`}{" "}
                      in the editor.
                    </p>
                  )}
                  {csvDroppedRows > 0 && (
                    <p className="text-amber-700" data-testid="csv-import-dropped">
                      {csvDroppedRows} of {csvPlan?.fileRows} rows in the file won't be kept (duplicates, or rows without
                      an area in a file that prices by area).
                    </p>
                  )}
                  {csvNeedsAreaColumn && (
                    <p className="text-red-600">
                      This plan prices per rating area â€” add a <code>rating_area</code> column so rows land in the right region.
                    </p>
                  )}
                  {csvParsed.warnings.map((w, i) => (
                    <p key={`w${i}`} className="text-amber-700" data-testid="csv-import-warning">{w}</p>
                  ))}
                  {csvParsed.errors.slice(0, 6).map((err, i) => (
                    <p key={i} className="text-red-600">{err}</p>
                  ))}
                  {csvParsed.errors.length > 6 && (
                    <p className="text-red-600">â€¦and {csvParsed.errors.length - 6} more issue{csvParsed.errors.length - 6 === 1 ? "" : "s"}.</p>
                  )}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => { setCsvImportOpen(false); setCsvText(""); }}>Cancel</Button>
                <Button
                  type="button"
                  disabled={!csvPlan || csvPlan.keptRows === 0 || csvNeedsAreaColumn}
                  onClick={applyCsvImport}
                >
                  Load {csvPlan?.keptRows || ""} rates into editor
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={addRegionOpen} onOpenChange={setAddRegionOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Add rating-area rate collection</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Adds a full set of rate rows priced for one rating area. At charge time each
                employer resolves the area from its primary location, locked at its plan-year start.
              </p>
              <div>
                <Label className="text-sm">State</Label>
                <Input
                  className="mt-1 w-24 uppercase"
                  maxLength={2}
                  placeholder="AZ"
                  value={regionState}
                  onChange={(e) => { setRegionState(e.target.value); setRegionAreaId(""); }}
                />
                {/* Clients already linked to this plan, so the broker doesn't
                    have to leave this dialog to go check each one's address -
                    click to fill the field above instead of typing it. */}
                {linkedStates.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1 mt-1.5">
                    <span className="text-xs text-muted-foreground">
                      {linkedStates.length === 1 ? "This plan's client is in:" : "This plan's clients are in:"}
                    </span>
                    {linkedStates.map((state) => (
                      <Button
                        key={state}
                        type="button"
                        variant={regionState.toUpperCase() === state ? "default" : "outline"}
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => { setRegionState(state); setRegionAreaId(""); }}
                      >
                        {state}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-sm">Rating area</Label>
                <Select value={regionAreaId} onValueChange={setRegionAreaId}>
                  <SelectTrigger className="mt-1">
                    <SelectValue
                      placeholder={
                        !/^[A-Za-z]{2}$/.test(regionState)
                          ? "Enter a state first"
                          : isFetchingStateAreas
                            ? "Loading areas..."
                            : stateAreas.length
                              ? "Select area"
                              : "No areas mapped for this state"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {stateAreas.map((a: any) => (
                      <SelectItem key={a.id} value={String(a.id)}>
                        {a.label || `Area ${a.areaNumber ?? a.id}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* Confirm step: show the picked area's counties so whoever is
                    copying a carrier's rate sheet into this form can check it
                    against the carrier's own county list before saving rates
                    under the wrong area (DEV-382/Twism: Area 12 got typed in
                    here when the carrier's sheet was actually for Area 13's
                    counties, and the bare "Area 12" label gave no way to tell). */}
                {regionAreaId && (() => {
                  const counties: string[] = stateAreas.find((a: any) => String(a.id) === regionAreaId)?.counties ?? [];
                  return counties.length > 0 ? (
                    <p className="text-xs text-muted-foreground mt-1">
                      Covers: {counties.join(", ")}
                    </p>
                  ) : null;
                })()}
                {/* This used to be a dead end: "No areas mapped for this
                    state" with nothing telling the broker what to do about
                    it. Only CA and a handful of county-based states
                    (TX/NY/FL/OR/UT/WA/AZ) have rating-area data loaded today
                    - anything else needs a superadmin to import it via the
                    Rate Areas admin tool before it can be used here. */}
                {/^[A-Za-z]{2}$/.test(regionState) && !isFetchingStateAreas && stateAreas.length === 0 && (
                  <p className="text-xs text-amber-700 mt-1">
                    {isSuperadminVariant ? (
                      <>
                        {regionState.toUpperCase()} has no rating areas set up yet.{" "}
                        <a
                          href="/superadmin/rate-areas"
                          target="_blank"
                          rel="noreferrer"
                          className="underline font-medium"
                        >
                          Add them in Rate Areas
                        </a>
                        , then come back here.
                      </>
                    ) : (
                      <>
                        {regionState.toUpperCase()} has no rating areas set up yet - ask a superadmin
                        to add them (Rate Areas admin tool) before pricing this region.
                      </>
                    )}
                  </p>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setAddRegionOpen(false)}>Cancel</Button>
                <Button type="button" disabled={!regionAreaId} onClick={addRegionRates}>Add Region</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <TabsContent value="benefits" className="space-y-4 pt-4">
          <div className="flex items-center justify-between">
            <Label>Plan Benefits & Coverage Details</Label>
            {!isTemplated && (
              <Button type="button" variant="outline" size="sm" onClick={addBenefit}>
                <Plus className="h-3.5 w-3.5 mr-1" /> Add Benefit
              </Button>
            )}
          </div>
          {isTemplated && (
            <TemplatedBenefitsEditor planType={type} networkType={networkType} value={rawBenefits} onChange={setRawBenefits} />
          )}
          {!isTemplated && (benefits.length === 0 ? (
            <p className="text-sm text-gray-400 py-8 text-center">No benefits added yet. Click "Add Benefit" to define coverage details.</p>
          ) : (
            <div className="space-y-4">
              {benefits.map((benefit, index) => (
                <Card key={index}>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <Select
                        value={benefit.benefitType}
                        onValueChange={(v) => updateBenefit(index, "benefitType", v)}
                      >
                        <SelectTrigger className="w-48">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="medical">Medical</SelectItem>
                          <SelectItem value="prescription">Prescription</SelectItem>
                          <SelectItem value="preventive">Preventive Care</SelectItem>
                          <SelectItem value="emergency">Emergency</SelectItem>
                          <SelectItem value="specialist">Specialist</SelectItem>
                          <SelectItem value="urgent_care">Urgent Care</SelectItem>
                          <SelectItem value="hospital">Hospital</SelectItem>
                          <SelectItem value="mental_health">Mental Health</SelectItem>
                          <SelectItem value="dental_basic">Dental Basic</SelectItem>
                          <SelectItem value="dental_major">Dental Major</SelectItem>
                          <SelectItem value="orthodontia">Orthodontia</SelectItem>
                          <SelectItem value="vision_exam">Vision Exam</SelectItem>
                          <SelectItem value="vision_frames">Vision Frames</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeBenefit(index)}>
                        <Trash2 className="h-4 w-4 text-gray-400" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">In-Network Deductible</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={benefit.inNetworkDeductible || ""}
                          onChange={(e) => updateBenefit(index, "inNetworkDeductible", e.target.value)}
                          placeholder="$0.00"
                          className="h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">In-Network Copay</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={benefit.inNetworkCopay || ""}
                          onChange={(e) => updateBenefit(index, "inNetworkCopay", e.target.value)}
                          placeholder="$0.00"
                          className="h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Max Out of Pocket</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={benefit.maxOutOfPocket || ""}
                          onChange={(e) => updateBenefit(index, "maxOutOfPocket", e.target.value)}
                          placeholder="$0.00"
                          className="h-8 text-sm"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">Out-of-Network Deductible</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={benefit.outNetworkDeductible || ""}
                          onChange={(e) => updateBenefit(index, "outNetworkDeductible", e.target.value)}
                          placeholder="$0.00"
                          className="h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">In-Network Coinsurance %</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={benefit.inNetworkCoinsurance || ""}
                          onChange={(e) => updateBenefit(index, "inNetworkCoinsurance", e.target.value)}
                          placeholder="0%"
                          className="h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Annual Maximum</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={benefit.annualMaximum || ""}
                          onChange={(e) => updateBenefit(index, "annualMaximum", e.target.value)}
                          placeholder="$0.00"
                          className="h-8 text-sm"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">Out-of-Network Copay</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={benefit.outNetworkCopay || ""}
                          onChange={(e) => updateBenefit(index, "outNetworkCopay", e.target.value)}
                          placeholder="$0.00"
                          className="h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Out-of-Network Coinsurance %</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={benefit.outNetworkCoinsurance || ""}
                          onChange={(e) => updateBenefit(index, "outNetworkCoinsurance", e.target.value)}
                          placeholder="0%"
                          className="h-8 text-sm"
                        />
                      </div>
                      <div className="flex items-center gap-2 pt-5">
                        <Switch
                          checked={!!benefit.deductibleApply}
                          onCheckedChange={(v) => updateBenefitFlag(index, "deductibleApply", v)}
                        />
                        <Label className="text-xs">Deductible applies first</Label>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Notes</Label>
                      <Input
                        value={benefit.description || ""}
                        onChange={(e) => updateBenefit(index, "description", e.target.value)}
                        placeholder="Additional coverage notes..."
                        className="h-8 text-sm"
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ))}

          {!isTemplated && rawBenefits.length > 0 && (
            <Card>
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm font-medium">
                  All benefit lines (raw)
                </CardTitle>
                <p className="text-xs text-gray-400">
                  Every line as the carrier sent it â€” the lossless record. The structured fields above are the curated, editable view.
                </p>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="space-y-1">
                  {rawBenefits.map((raw, index) => (
                    <div key={index} className="flex items-center justify-between gap-3 text-sm border-b border-gray-100 py-1 last:border-0">
                      <span className="text-gray-600 flex items-center gap-2">
                        {raw.name}
                        {raw.network === "out" && (
                          <Badge variant="outline" className="text-[10px]">out-of-network</Badge>
                        )}
                        {raw.deductibleApply && (
                          <Badge variant="outline" className="text-[10px]">deductible applies</Badge>
                        )}
                      </span>
                      <span className="font-medium text-gray-800">{raw.value || "â€”"}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {isSuperadminVariant && (
        <TabsContent value="forms" className="space-y-4 pt-4">
          {!planId ? (
            <div className="space-y-4">
              <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-200">
                Pick the carrier forms this plan requires â€” they attach automatically when the plan
                is created.
              </div>
              {pendingForms.length > 0 && (
                <div>
                  <Label className="text-sm font-medium">Forms to attach ({pendingForms.length})</Label>
                  <div className="space-y-2 mt-2">
                    {pendingForms.map((f) => (
                      <div key={f.id} className="flex items-center justify-between border rounded-md p-3">
                        <div className="flex items-center gap-3">
                          <FileText className="h-4 w-4 text-gray-400" />
                          <div>
                            <p className="text-sm font-medium">{f.name}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <Badge variant="outline" className="text-xs">{f.formType}</Badge>
                              {f.carrierName && <span className="text-xs text-gray-500">{f.carrierName}</span>}
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPendingForms((prev) => prev.filter((p) => p.id !== f.id))}
                        >
                          <Trash2 className="h-4 w-4 text-red-400" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <Label className="text-sm font-medium">Available Forms</Label>
                <Input
                  className="mt-2"
                  placeholder="Search forms..."
                  value={formSearch}
                  onChange={(e) => setFormSearch(e.target.value)}
                />
                {!carrierId && (
                  <p className="text-xs text-gray-400 mt-1">
                    Tip: pick a carrier in Plan Details to narrow this list to that carrier's forms.
                  </p>
                )}
                <div className="space-y-2 mt-2 max-h-64 overflow-y-auto">
                  {pickableForms.filter((f) => !pendingForms.some((p) => p.id === f.id)).length === 0 ? (
                    <p className="text-sm text-gray-400 py-4 text-center">No matching forms.</p>
                  ) : (
                    pickableForms
                      .filter((f) => !pendingForms.some((p) => p.id === f.id))
                      .slice(0, 25)
                      .map((f) => (
                        <div key={f.id} className="flex items-center justify-between border rounded-md p-3">
                          <div className="flex items-center gap-3">
                            <FileText className="h-4 w-4 text-gray-400" />
                            <div>
                              <p className="text-sm font-medium">{f.name}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <Badge variant="outline" className="text-xs">{f.formType}</Badge>
                                {f.carrierName && <span className="text-xs text-gray-500">{f.carrierName}</span>}
                              </div>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPendingForms((prev) => [...prev, f])}
                          >
                            <Plus className="h-3.5 w-3.5 mr-1" /> Add
                          </Button>
                        </div>
                      ))
                  )}
                </div>
              </div>
            </div>
          ) : (
          <>
          <PlanDocumentsManager planId={planId} />
          <Separator />
          <div>
            <Label className="text-sm font-medium">Attached Forms</Label>
            {attachedForms.length === 0 ? (
              <p className="text-sm text-gray-400 py-4 text-center">No forms attached to this plan yet.</p>
            ) : (
              <div className="space-y-2 mt-2">
                {attachedForms.map((att: any) => (
                  <div key={att.id} className="flex items-center justify-between border rounded-md p-3">
                    <div className="flex items-center gap-3">
                      <FileText className="h-4 w-4 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium">{att.form?.name || "Unknown Form"}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge variant="outline" className="text-xs">{att.form?.formType}</Badge>
                          {att.form?.carrierName && <span className="text-xs text-gray-500">{att.form.carrierName}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1.5">
                        <Label className="text-xs text-gray-500">Required</Label>
                        <Switch checked={att.isRequired} disabled />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFormMutation.mutate(att.id)}
                        disabled={removeFormMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4 text-red-400" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <Separator />
          <div>
            <Label className="text-sm font-medium">Suggested Forms</Label>
            {suggestedForms.length === 0 ? (
              <p className="text-sm text-gray-400 py-4 text-center">No suggested forms available.</p>
            ) : (
              <div className="space-y-2 mt-2">
                {suggestedForms.map((suggestion: any) => (
                  <div key={suggestion.form.id} className="flex items-center justify-between border rounded-md p-3">
                    <div className="flex items-center gap-3">
                      <FileText className="h-4 w-4 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium">{suggestion.form.name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge variant="outline" className="text-xs">{suggestion.form.formType}</Badge>
                          {suggestion.form.carrierName && <span className="text-xs text-gray-500">{suggestion.form.carrierName}</span>}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => attachFormMutation.mutate(suggestion.form.id)}
                      disabled={attachFormMutation.isPending}
                    >
                      <Plus className="h-3.5 w-3.5 mr-1" /> Attach
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          </>
          )}
        </TabsContent>
        )}
      </Tabs>

      <Separator />

      <Button
        className="w-full"
        onClick={handleSubmit}
        disabled={!isValid || isPending}
      >
        {isPending ? "Saving..." : initialPlan ? "Save Changes" : "Create Plan"}
      </Button>
      {missingReasons.length > 0 && !isPending && (
        <p className="text-xs text-center text-muted-foreground" data-testid="plan-builder-missing">
          Missing: {missingReasons.join(", ")}
        </p>
      )}

      <AlertDialog open={confirmStructureChangeOpen} onOpenChange={setConfirmStructureChangeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace this plan's current rates?</AlertDialogTitle>
            <AlertDialogDescription>
              This plan's current rates use "
              {RATE_STRUCTURE_TYPES.find((r) => r.value === originalStructureType)?.label ?? originalStructureType}
              ". Saving as "
              {RATE_STRUCTURE_TYPES.find((r) => r.value === rateStructureType)?.label ?? rateStructureType}
              " replaces the plan's current rates for this period - the old ones won't be recoverable.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmStructureChangeOpen(false);
                submitSave();
              }}
            >
              Replace Rates
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmRemoveRegionOpen} onOpenChange={setConfirmRemoveRegionOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this region's rates?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes all {selectedRegionRowCount} rate rows for {selectedRegionLabel} from the
              editor. It isn't saved until you hit Save, but there's no undo once you do.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmRemoveRegionOpen(false);
                removeRegionRates(selectedRatingArea);
              }}
            >
              Remove Region
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
